import {
  defaultBasemapStyle,
  EditorMode,
  LoadingState,
  type MapBounds,
  type MapDetail,
  type MapObject,
  type StreetViewMarkerObject,
  type TLoadedState,
  type TLoadingState,
  type UnitSystem,
} from '~/common/types';

export { MapComponent } from './Map';

// BasicMapStore related imports
import { DateTime } from 'luxon';
import type mapboxgl from 'mapbox-gl';
import type { MapRef } from 'react-map-gl/mapbox';
import { ref } from 'valtio';
import type { LngLat } from './canvas/SurfaceBase';
import type { SidebarSection } from './Widgets/MetadataSidebar/buildSidebarSections';

export type StreetViewState =
  | {
      visible: false;
    }
  | {
      visible: true;
      isNew: true;
      lng: number;
      lat: number;
      heading: number;
      pitch: number;
      zoom: number;
    }
  | {
      visible: true;
      isNew: false;
      marker: StreetViewMarkerObject;
    };

export interface CopyMetadataOptions {
  fields: { field: string; value: any }[];
  map: MapRef;
  lngLat: LngLat;
  color: string;
  minZoom: number;
}

// Matches valtio's own `ref()` return type / `Snapshot<T>` check (`T extends { $$valtioSnapshot: infer S }
// ? S : ...`), so a field declared with this type resolves back to plain `T` through useSnapshot
// instead of recursing into a deep-readonly Snapshot<T> shape.
type Reffed<T extends object> = T & { $$valtioSnapshot: T };

// projectId scopes the cache to the project it was built for, so a Project A -> B remount can
// never render A's stale sections on B even if the point coincidentally matches (CG-850).
export type IdentifyDetailsSectionsCache = {
  point: { lng: number; lat: number };
  sections: SidebarSection[];
  projectId: string;
};

// The identify point each store last announced, held outside valtio so the live point isn't wrapped or
// tracked; a sidebar remount for the same point then stays silent.
const lastAnnouncedPoints = new WeakMap<MapStoreBase, object>();

export abstract class MapStoreBase {
  currentObjectId?: string;
  currentMap: TLoadingState | (MapDetail & TLoadedState) = LoadingState;
  hiddenObjects: string[] = [];
  editorMode: EditorMode = EditorMode.None;
  streetViewState: StreetViewState = { visible: false };
  shouldRedraw = false;
  identifyDetailsPoint: undefined | { lng: number; lat: number } = undefined;
  reportProblemPoint: undefined | { lng: number; lat: number } = undefined;
  // Set on left-click single-select to the clicked section's id; undefined = area mode
  // (right-click, close, Escape, custom-data). The sidebar filters to this one section.
  selectedFeatureKey: string | undefined = undefined;
  // Last resolved sections for identifyDetailsPoint, kept so a remount (e.g. closing Project
  // Info) can render from cache while MapLayers is still re-registering layers (CG-850).
  identifyDetailsSectionsCache: Reffed<IdentifyDetailsSectionsCache> | undefined = undefined;
  // Screen-reader status for map selection; `id` changes on every announcement so repeated text is spoken again.
  selectionAnnouncement: { text: string; id: number } | undefined = undefined;
  mapBounds?: MapBounds;

  abstract get currentObject(): MapObject | undefined;
  abstract get objectsOnMainSurface(): { [key: string]: MapObject };
  abstract get currentUnitSystem(): UnitSystem;

  abstract setEditorMode(mode: EditorMode): void;
  abstract generateSortOrderKey(): string;
  abstract setCurrentObjectId(objectId: string): void;
  abstract resetCurrentObjectId(): void;
  abstract newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject?: boolean,
    map?: mapboxgl.Map | undefined,
  ): Promise<MapObject | undefined>;
  abstract updateObject<T = MapObject>(o: Partial<T>): Promise<void>;
  abstract deleteObject<T = MapObject>(o: Partial<T>, shouldPushToUndoStack?: boolean): Promise<MapObject | undefined>;

  setStreetViewState(streetViewState: StreetViewState) {
    this.streetViewState = streetViewState;
  }

  setIdentifyDetailsPoint(point: undefined | { lng: number; lat: number }, selectedFeatureKey?: string) {
    this.identifyDetailsPoint = point;
    this.selectedFeatureKey = selectedFeatureKey;
    // Drop the stale cache on any new/blank point — it's only valid for the point it was built for.
    this.identifyDetailsSectionsCache = undefined;
    if (!point) lastAnnouncedPoints.delete(this);
  }

  announceSelection(text: string) {
    this.selectionAnnouncement = { text, id: (this.selectionAnnouncement?.id ?? 0) + 1 };
  }

  /** Announces `text` for an identify `point` once; a repeat call for the same point object is ignored. */
  announceSelectionFor(point: { lng: number; lat: number }, text: string) {
    if (lastAnnouncedPoints.get(this) === point) return;
    lastAnnouncedPoints.set(this, point);
    this.announceSelection(text);
  }

  // `ref()` keeps the sections array + geometry un-proxied (and not DeepReadonly), since the
  // sidebar reads them back as plain mutable SidebarSection[] via resolveSidebarSections (CG-850).
  setIdentifyDetailsSectionsCache(cache: IdentifyDetailsSectionsCache | undefined) {
    this.identifyDetailsSectionsCache = cache ? ref(cache) : undefined;
  }

  setMapBounds(bounds?: MapBounds) {
    this.mapBounds = bounds;
  }

  setReportProblemPoint(point: undefined | { lng: number; lat: number }) {
    this.reportProblemPoint = point;
  }

  copyMetadataToCanvas(title: string, options: CopyMetadataOptions) {}
}

export class EmptyStore extends MapStoreBase {
  get currentObject(): MapObject | undefined {
    return undefined;
  }
  get objectsOnMainSurface(): { [key: string]: MapObject } {
    return {};
  }
  get currentUnitSystem(): UnitSystem {
    throw new Error('Method not implemented.');
  }
  setEditorMode(mode: EditorMode): void {
    throw new Error('Method not implemented.');
  }
  generateSortOrderKey(): string {
    throw new Error('Method not implemented.');
  }
  setCurrentObjectId(objectId: string): void {
    throw new Error('Method not implemented.');
  }
  resetCurrentObjectId(): void {
    throw new Error('Method not implemented.');
  }
  newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject?: boolean | undefined,
    map?: mapboxgl.Map | undefined,
  ): Promise<MapObject | undefined> {
    throw new Error('Method not implemented.');
  }
  updateObject<T = MapObject>(o: Partial<T>): Promise<void> {
    throw new Error('Method not implemented.');
  }
  deleteObject<T = MapObject>(o: T, shouldPushToUndoStack: boolean): Promise<MapObject | undefined> {
    throw new Error('Method not implemented.');
  }
}

// NOTE:
// The BasicMapStore is for the Measure tool on the Map. In order to create a store separate
// from the store passed into the Map as a prop, we need to create a separate MapStore.
// Needs to be created here in order to avoid the error thrown when instantiating a class
// prior to class initialization.
export class BasicMapStore extends MapStoreBase {
  editorMode: EditorMode = EditorMode.None;
  currentMap: TLoadingState | (MapDetail & TLoadedState) = {
    isLoading: false,
    id: 'temp',
    name: 'temp',
    image: 'temp',
    unitSystem: 'imperial',
    baseMap: defaultBasemapStyle,
    createdAt: DateTime.now(),
    createdBy: {
      name: 'temp',
    },
    updatedAt: DateTime.now(),
    updatedBy: {
      name: 'temp',
    },
    objects: {},
  };

  get currentObject() {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      return this.currentMap.objects[this.currentObjectId];
    }
    return undefined;
  }

  get objectsOnMainSurface(): { [key: string]: MapObject } {
    return {};
  }

  get currentUnitSystem(): UnitSystem {
    return 'imperial';
  }

  setEditorMode(mode: EditorMode): void {
    if (mode === EditorMode.None) {
      this.currentObjectId = undefined;
    }
    this.editorMode = mode;
  }

  generateSortOrderKey(): string {
    return 'abc'; // this is not need for drawing
  }

  setCurrentObjectId(objectId: string): void {
    if (!this.currentMap.isLoading) {
      const object = this.currentMap.objects[objectId];
      if (object) {
        this.currentObjectId = object.id;
        if (object.type === 'polygon') {
          this.setEditorMode(EditorMode.EditPolygon);
        } else if (object.type === 'line') {
          this.setEditorMode(EditorMode.EditLine);
        }
      }
    }
  }

  resetCurrentObjectId(): void {
    // do nothing
  }

  async newObject(
    o: Omit<MapObject, 'createdBy' | 'updatedBy'>,
    setCurrentObject?: boolean | undefined,
    map?: mapboxgl.Map | undefined,
  ): Promise<MapObject | undefined> {
    return undefined;
  }

  async updateObject<T = MapObject>(o: Partial<T>): Promise<void> {}

  deleteObject<T = MapObject>(o: T, shouldPushToUndoStack: boolean): Promise<MapObject | undefined> {
    throw new Error('Method not implemented.');
  }
}
